import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IconPlus,
  IconTrash,
  IconStar,
  IconStarFilled,
} from '@tabler/icons-react';
import type {
  CheckInTermsIntakeType,
  CheckInTermsTemplate,
  CheckInTermsTemplateCreate,
  CheckInTermsTemplateUpdate,
  TermsBodyFormat,
} from '@techgitter/contracts';
import {
  CHECKIN_TEMPLATE_VARIABLES,
  TERMS_BODY_FORMAT_DEFAULT,
  normaliseTermsBodyFormat,
} from '@techgitter/contracts';

import {
  invalidateCheckInTermsTemplatesCache,
  useCheckInTermsTemplates,
} from '../hooks/useCheckInTermsTemplates';
import {
  createCheckInTermsTemplate,
  deleteCheckInTermsTemplate,
  setDefaultCheckInTermsTemplate,
  updateCheckInTermsTemplate,
} from '../services/checkInTermsTemplatesApi';
import {
  SecondaryToolbar,
  ToolbarButton,
  ToolbarCount,
  ToolbarSpacer,
} from '../components/toolbar';
import { Chip } from '../components/chip';
import { TermsMarkdown } from '../components/common/TermsMarkdown';
import {
  TermsFormatToggle,
  UnknownVariableWarning,
  VariablesPanel,
  insertAtCursor,
} from '../components/admin/TermsEditorControls';
import { useAppSettings } from '../hooks/useAppSettings';
import { renderCheckInTemplate } from '../utils/checkInTemplateRenderer';
import {
  CARD_SX,
  FIELD_SX,
  PRIMARY_BTN_SX,
} from '../styles/designSystem';

/**
 * Check-In T&C template editor.
 *
 * Left rail: templates grouped by intake type (In-store / Online), each
 * group carrying its own default indicator.
 * Right pane: editor (name / version / body / closer). The body is static
 *             prose shown verbatim to the customer — no {{variables}}.
 *
 * The default-flag invariant is enforced server-side **per intake type**
 * (one default for in-store, one for online); the page just calls
 * ``set-default`` and refetches. ``intakeType`` and ``slug`` are set at
 * creation and are not editable.
 *
 * Card-on-grey design system (sm4rt-web-ui skill): a floating toolbar over a
 * grey surface, CARD_SX cards for the list rail / editor / preview, shared
 * Chip badges and Tabler icons.
 */

const INTAKE_ORDER: CheckInTermsIntakeType[] = ['in_store', 'online'];
const INTAKE_LABELS: Record<CheckInTermsIntakeType, string> = {
  in_store: 'In-store',
  online: 'Online (remote)',
};

const todayVersion = (): string => new Date().toISOString().slice(0, 10);

export const CheckInTermsTemplatesAdminPage: React.FC = () => {
  const { templates, isLoading, error } = useCheckInTermsTemplates();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditorDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] =
    useState<CheckInTermsTemplate | null>(null);
  const [newDialog, setNewDialog] = useState<NewDialogState | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Default-select the first template when the catalog lands; preserve the
  // current pick when the list refetches after a save.
  useEffect(() => {
    if (!templates.length) {
      setSelectedId(null);
      setDraft(null);
      return;
    }
    if (selectedId && templates.some((t) => t.id === selectedId)) {
      const t = templates.find((x) => x.id === selectedId)!;
      setDraft(toDraft(t));
      return;
    }
    const target = templates.find((t) => t.isDefault) || templates[0];
    setSelectedId(target.id);
    setDraft(toDraft(target));
  }, [templates, selectedId]);

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  );

  const grouped = useMemo(() => {
    const out: Record<CheckInTermsIntakeType, CheckInTermsTemplate[]> = {
      in_store: [],
      online: [],
    };
    for (const t of templates) {
      if (t.intakeType === 'in_store' || t.intakeType === 'online') {
        out[t.intakeType].push(t);
      }
    }
    return out;
  }, [templates]);

  const dirty = useMemo(() => {
    if (!draft || !selected) return false;
    return (
      draft.name !== selected.name ||
      draft.version !== selected.version ||
      (draft.description ?? '') !== (selected.description ?? '') ||
      draft.body !== selected.body ||
      (draft.closer ?? '') !== (selected.closer ?? '')
    );
  }, [draft, selected]);

  const handleSelect = (id: string) => {
    if (dirty) {
      const confirm = window.confirm(
        'Unsaved changes will be discarded. Continue?',
      );
      if (!confirm) return;
    }
    setSelectedId(id);
    const t = templates.find((x) => x.id === id);
    setDraft(t ? toDraft(t) : null);
  };

  // App settings feed the preview's business-hours variables, so an admin
  // sees this business's real opening times rather than the defaults.
  const { settings } = useAppSettings();

  // The variables panel inserts at the caret, so it needs the live textarea.
  const bodyFieldRef = React.useRef<HTMLTextAreaElement | null>(null);

  const handleSave = async () => {
    if (!selected || !draft || !dirty) return;
    setSaving(true);
    setSaveError(null);
    const patch: CheckInTermsTemplateUpdate = {
      name: draft.name,
      version: draft.version,
      description: draft.description || undefined,
      body: draft.body,
      closer: draft.closer || undefined,
      bodyFormat: draft.bodyFormat,
    };
    try {
      await updateCheckInTermsTemplate(selected.id, patch);
      invalidateCheckInTermsTemplatesCache();
      setSnackbar({ open: true, message: 'Template saved', severity: 'success' });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (payload: CheckInTermsTemplateCreate) => {
    setSaving(true);
    setSaveError(null);
    try {
      const created = await createCheckInTermsTemplate(payload);
      invalidateCheckInTermsTemplatesCache();
      setSelectedId(created.id);
      setNewDialog(null);
      setSnackbar({ open: true, message: 'Template created', severity: 'success' });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      await setDefaultCheckInTermsTemplate(id);
      invalidateCheckInTermsTemplatesCache();
      setSnackbar({ open: true, message: 'Default updated', severity: 'success' });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    setSaveError(null);
    try {
      await deleteCheckInTermsTemplate(confirmDelete.id);
      invalidateCheckInTermsTemplatesCache();
      if (selectedId === confirmDelete.id) setSelectedId(null);
      setConfirmDelete(null);
      setSnackbar({ open: true, message: 'Template deleted', severity: 'success' });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarCount>
          {templates.length} template{templates.length === 1 ? '' : 's'}
        </ToolbarCount>
        <ToolbarSpacer />
        <ToolbarButton
          variant="primary"
          icon={IconPlus}
          onClick={() =>
            setNewDialog({
              slug: '',
              name: '',
              body: '',
              intakeType: 'in_store',
              version: todayVersion(),
              bodyFormat: TERMS_BODY_FORMAT_DEFAULT,
            })
          }
        >
          New template
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={[CARD_SX, { p: 3, maxWidth: 720 }]}>
            <Typography sx={{ fontSize: 13, color: '#666' }}>
              No templates yet — click <strong>New template</strong> to create
              one. In-store and remote customers each need their own default
              template before the public intake can show terms.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            {/* Left rail — templates grouped by intake type */}
            <Box sx={[CARD_SX, { width: 260, flexShrink: 0, overflow: 'hidden', p: 0.5 }]}>
              {INTAKE_ORDER.map((intake) => (
                <Box key={intake} sx={{ mb: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#646464',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      px: 1.25,
                      py: 0.75,
                    }}
                  >
                    {INTAKE_LABELS[intake]}
                  </Typography>
                  {grouped[intake].length === 0 ? (
                    <Typography sx={{ fontSize: 12, color: '#aaa', px: 1.25, pb: 0.75 }}>
                      No templates
                    </Typography>
                  ) : (
                    grouped[intake].map((t) => {
                      const active = t.id === selectedId;
                      return (
                        <Box
                          key={t.id}
                          onClick={() => handleSelect(t.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            py: 1,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            bgcolor: active ? '#E6F1FB' : 'transparent',
                            '&:hover': { bgcolor: active ? '#E6F1FB' : '#f7f9fc' },
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: active ? 500 : 400,
                                color: active ? '#0C447C' : '#1a1a1a',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {t.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: '#888',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {t.slug} · v{t.version}
                            </Typography>
                          </Box>
                          {t.isDefault && (
                            <Tooltip title={`Default ${INTAKE_LABELS[intake]} template`}>
                              <Box
                                component="span"
                                sx={{ color: '#EF9F27', display: 'inline-flex', flexShrink: 0 }}
                              >
                                <IconStarFilled size={13} />
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>
              ))}
            </Box>

            {/* Right pane — editor */}
            {draft && selected ? (
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Box sx={[CARD_SX, { p: 2 }]}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <TextField
                      label="Name"
                      size="small"
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      sx={[FIELD_SX, { flexGrow: 1 }]}
                    />
                    <Chip tone="info">{INTAKE_LABELS[selected.intakeType]}</Chip>
                    <Chip tone="neutral">slug: {selected.slug}</Chip>
                    <Tooltip
                      title={
                        selected.isDefault
                          ? `Currently the default ${INTAKE_LABELS[selected.intakeType]} template`
                          : `Mark as default for ${INTAKE_LABELS[selected.intakeType]}`
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={selected.isDefault || saving}
                          onClick={() => handleSetDefault(selected.id)}
                          sx={{ color: selected.isDefault ? '#EF9F27' : '#bbb' }}
                        >
                          {selected.isDefault ? (
                            <IconStarFilled size={17} />
                          ) : (
                            <IconStar size={17} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        selected.isDefault
                          ? 'Cannot delete the default — mark another template as default first.'
                          : 'Delete template'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={selected.isDefault || saving}
                          onClick={() => setConfirmDelete(selected)}
                          sx={{ color: '#bbb', '&:hover': { color: '#B03A2E' } }}
                        >
                          <IconTrash size={17} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Version"
                      size="small"
                      value={draft.version}
                      onChange={(e) => setDraft({ ...draft, version: e.target.value })}
                      helperText="Recorded on each customer's acceptance. Bump when the terms change materially (e.g. a date or v-number)."
                      sx={[FIELD_SX, { maxWidth: 280 }]}
                    />
                    <TextField
                      label="Description (admin only — not shown to customers)"
                      size="small"
                      value={draft.description ?? ''}
                      onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      sx={FIELD_SX}
                    />
                    <TermsFormatToggle
                      value={draft.bodyFormat}
                      onChange={(bodyFormat) => setDraft({ ...draft, bodyFormat })}
                      warnOnSwitch={
                        draft.bodyFormat !==
                        normaliseTermsBodyFormat(selected.bodyFormat)
                      }
                    />
                    <UnknownVariableWarning
                      sources={[draft.body, draft.closer]}
                      variables={CHECKIN_TEMPLATE_VARIABLES}
                    />
                    <TextField
                      label="Body (shown to the customer)"
                      multiline
                      minRows={10}
                      value={draft.body}
                      inputRef={bodyFieldRef}
                      onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                      helperText="Supports {{path}} and {{#if path}}…{{/if}} — see the variables panel. Bump the version when the wording changes materially."
                      sx={FIELD_SX}
                    />
                    <TextField
                      label="Closer (optional friendly sign-off)"
                      multiline
                      minRows={2}
                      value={draft.closer ?? ''}
                      onChange={(e) => setDraft({ ...draft, closer: e.target.value })}
                      sx={FIELD_SX}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!dirty || saving}
                        sx={PRIMARY_BTN_SX}
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </Button>
                    </Box>
                  </Box>
                </Box>

                <PreviewPanel
                  draft={draft}
                  intakeType={selected.intakeType}
                  appSettings={settings}
                />

                <VariablesPanel
                  variables={CHECKIN_TEMPLATE_VARIABLES}
                  groups={['session', 'customer', 'appSettings', 'system']}
                  onInsert={(snippet) =>
                    setDraft({
                      ...draft,
                      body: insertAtCursor(
                        bodyFieldRef.current,
                        draft.body,
                        snippet,
                      ),
                    })
                  }
                />
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: 13, color: '#888' }}>
                  Select a template from the list to edit.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {confirmDelete && (
        <Dialog open onClose={() => setConfirmDelete(null)}>
          <DialogTitle>Delete template?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete <strong>{confirmDelete.name}</strong>? The template is
              soft-deleted — its slug stays reserved, and any acceptance a
              customer already made keeps its own frozen snapshot. Make sure
              another {INTAKE_LABELS[confirmDelete.intakeType]} template is the
              default first.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button sx={CANCEL_BTN_SX} onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} disabled={saving}>
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {newDialog && (
        <NewTemplateDialog
          state={newDialog}
          onChange={setNewDialog}
          onClose={() => setNewDialog(null)}
          onCreate={handleCreate}
          saving={saving}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

/** Recessive grey Cancel/Back button (sm4rt-web-ui skill §9 — never blue). */
const CANCEL_BTN_SX = {
  color: '#888',
  '&:hover': { color: '#555', bgcolor: 'transparent' },
};

interface EditorDraft {
  name: string;
  version: string;
  description?: string;
  body: string;
  closer?: string;
  bodyFormat: TermsBodyFormat;
}

function toDraft(t: CheckInTermsTemplate): EditorDraft {
  return {
    name: t.name,
    version: t.version,
    description: t.description,
    body: t.body,
    closer: t.closer,
    // Normalised, not read raw: where the Dataverse column has not been
    // created yet the field is undefined, which means 'text'.
    bodyFormat: normaliseTermsBodyFormat(t.bodyFormat),
  };
}

interface PreviewPanelProps {
  draft: EditorDraft;
  intakeType: CheckInTermsIntakeType;
  appSettings: import('@techgitter/contracts').AppSettings | null;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  draft,
  intakeType,
  appSettings,
}) => {
  const body = renderCheckInTemplate(draft.body, intakeType, appSettings);
  const closer = renderCheckInTemplate(draft.closer, intakeType, appSettings);
  return (
    <Box sx={[CARD_SX, { p: 2 }]}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
        Preview (as the customer sees it)
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#888', display: 'block', mb: 1 }}>
        {intakeType === 'in_store'
          ? 'Sample in-store session. The customer has not given their details yet at this point in the flow, so customer variables show their fallbacks.'
          : 'Sample online session, with the customer already on the record.'}
      </Typography>
      <Box sx={{ borderTop: '0.5px solid #e2eaf3', mb: 1.5 }} />
      {body ? (
        <TermsMarkdown
          body={body}
          format={draft.bodyFormat}
          sx={{ fontSize: 13, color: '#666' }}
        />
      ) : (
        <Typography sx={{ fontSize: 13, color: '#666' }}>—</Typography>
      )}
      {closer && (
        <TermsMarkdown
          body={closer}
          format={draft.bodyFormat}
          sx={{ fontSize: 13, color: '#333', mt: 1.5 }}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// New-template dialog
// ---------------------------------------------------------------------------

interface NewDialogState {
  slug: string;
  name: string;
  body: string;
  intakeType: CheckInTermsIntakeType;
  version: string;
  description?: string;
  closer?: string;
  bodyFormat: TermsBodyFormat;
  isDefault?: boolean;
}

interface NewTemplateDialogProps {
  state: NewDialogState;
  onChange: (s: NewDialogState) => void;
  onClose: () => void;
  onCreate: (payload: CheckInTermsTemplateCreate) => void;
  saving: boolean;
}

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

const NewTemplateDialog: React.FC<NewTemplateDialogProps> = ({
  state,
  onChange,
  onClose,
  onCreate,
  saving,
}) => {
  const canCreate =
    state.slug.trim() !== '' &&
    state.name.trim() !== '' &&
    state.body.trim() !== '' &&
    state.version.trim() !== '';
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New check-in terms template</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <TextField
            select
            label="Intake type"
            size="small"
            value={state.intakeType}
            sx={FIELD_SX}
            onChange={(e) =>
              onChange({
                ...state,
                intakeType: e.target.value as CheckInTermsIntakeType,
              })
            }
            helperText="In-store and remote customers get different terms. Set at creation and not editable afterwards."
          >
            {INTAKE_ORDER.map((intake) => (
              <MenuItem key={intake} value={intake}>
                {INTAKE_LABELS[intake]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Name"
            size="small"
            value={state.name}
            sx={FIELD_SX}
            onChange={(e) => {
              const name = e.target.value;
              const suggested = slugify(name);
              onChange({
                ...state,
                name,
                slug:
                  state.slug === '' || state.slug === slugify(state.name)
                    ? suggested
                    : state.slug,
              });
            }}
          />
          <TextField
            label="Slug"
            size="small"
            value={state.slug}
            sx={FIELD_SX}
            onChange={(e) => onChange({ ...state, slug: slugify(e.target.value) })}
            helperText="Stable identifier. Lowercase letters, digits, dash, underscore."
          />
          <TextField
            label="Version"
            size="small"
            value={state.version}
            sx={FIELD_SX}
            onChange={(e) => onChange({ ...state, version: e.target.value })}
            helperText="Recorded on each customer's acceptance (e.g. a date or v-number)."
          />
          <TextField
            label="Description (admin only)"
            size="small"
            value={state.description ?? ''}
            sx={FIELD_SX}
            onChange={(e) => onChange({ ...state, description: e.target.value })}
          />
          <TermsFormatToggle
            value={state.bodyFormat}
            onChange={(bodyFormat) => onChange({ ...state, bodyFormat })}
          />
          <TextField
            label="Body (shown to the customer)"
            multiline
            minRows={6}
            value={state.body}
            sx={FIELD_SX}
            onChange={(e) => onChange({ ...state, body: e.target.value })}
          />
          <TextField
            label="Closer"
            multiline
            minRows={2}
            value={state.closer ?? ''}
            sx={FIELD_SX}
            onChange={(e) => onChange({ ...state, closer: e.target.value })}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={Boolean(state.isDefault)}
              onChange={(e) => onChange({ ...state, isDefault: e.target.checked })}
            />
            <Typography sx={{ fontSize: 13 }}>
              Make default for {INTAKE_LABELS[state.intakeType]}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button sx={CANCEL_BTN_SX} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={PRIMARY_BTN_SX}
          onClick={() =>
            onCreate({
              slug: state.slug,
              name: state.name,
              intakeType: state.intakeType,
              version: state.version,
              body: state.body,
              description: state.description || undefined,
              closer: state.closer || undefined,
              bodyFormat: state.bodyFormat,
              isDefault: state.isDefault,
            })
          }
          disabled={!canCreate || saving}
        >
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
